import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Video, Trash2, Plus, Loader2, Edit3, Eye, EyeOff, X, Save } from "lucide-react";
import VideoUploader from "../components/VideoUploader";

export default function PremiumContentAdmin() {
  const [params] = useSearchParams();
  const initialProfile = params.get("profile") || "";
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(initialProfile);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "" });
  const [video, setVideo] = useState(null); // { secure_url, public_id, duration }
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/premium/profiles").then(r => {
      setProfiles(r.data || []);
      if (!profileId && r.data?.length) setProfileId(r.data[0].id);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContent = (pid) => {
    if (!pid) return;
    setLoading(true);
    api.get(`/premium/profiles/${pid}/content`).then(r => setContent(r.data.content || [])).finally(() => setLoading(false));
  };

  useEffect(() => { loadContent(profileId); }, [profileId]);

  const add = async (e) => {
    e?.preventDefault?.();
    if (!form.title || !video) { setMsg("Pick a video and add a title"); return; }
    setBusy(true); setMsg("");
    try {
      const payload = {
        profile_id: profileId,
        title: form.title,
        video_url: video.secure_url,
        cloudinary_public_id: video.public_id,
        duration_seconds: Math.round(video.duration || 60),
      };
      await api.post("/premium/content", payload);
      setForm({ title: "" });
      setVideo(null);
      setMsg("✓ Video added to this premium profile");
      loadContent(profileId);
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try {
      await api.delete(`/premium/content/${c.id}`);
      loadContent(profileId);
    } catch (e) { window.alert(e?.response?.data?.detail || "Delete failed"); }
  };

  // Edit modal state
  const [editing, setEditing] = useState(null); // content row being edited
  const [editForm, setEditForm] = useState({ title: "", price_goldly: 0, duration_seconds: 60, is_hidden: false });
  const [saving, setSaving] = useState(false);

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({
      title: c.title || "",
      price_goldly: c.price_goldly || 0,
      duration_seconds: c.duration_seconds || 60,
      is_hidden: !!c.is_hidden,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/premium/content/${editing.id}`, {
        title: editForm.title,
        price_goldly: parseInt(editForm.price_goldly) || 0,
        duration_seconds: parseInt(editForm.duration_seconds) || 60,
        is_hidden: !!editForm.is_hidden,
      });
      setEditing(null);
      loadContent(profileId);
    } catch (e) { window.alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleHide = async (c) => {
    try {
      await api.patch(`/premium/content/${c.id}`, { is_hidden: !c.is_hidden });
      loadContent(profileId);
    } catch (e) { window.alert(e?.response?.data?.detail || "Failed"); }
  };


  const activeProfile = profiles.find(p => p.id === profileId);

  return (
    <div className="px-4 py-6 pb-24" data-testid="premium-content-admin-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/dashboard" data-testid="pca-back" className="p-2 rounded-full bg-white/5"><ArrowLeft className="w-4 h-4"/></Link>
        <h1 className="font-outfit font-extrabold text-2xl flex items-center gap-2">
          <Video className="w-6 h-6 text-[#00E5FF]"/>Premium Content
        </h1>
      </div>

      <div className="mb-2 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 p-2.5 text-[11px] text-white/80">
        <b className="text-[#00E5FF]">Note:</b> Videos uploaded here go into the selected <b>Premium profile&apos;s exclusive library</b>. They do NOT show up in your personal reel feed.
      </div>

      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Choose Profile</label>
      <select value={profileId} onChange={e => setProfileId(e.target.value)} data-testid="pca-profile-select"
        className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
        {profiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.content_count || 0} videos)</option>)}
      </select>

      {activeProfile && (
        <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3" data-testid="pca-active-profile">
          {activeProfile.cover_image && <img src={activeProfile.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover"/>}
          <div className="text-xs">
            <div className="font-bold">{activeProfile.name}</div>
            <div className="text-white/50 text-[10px]">{activeProfile.category} · {activeProfile.price_goldly} Goldly unlock</div>
          </div>
        </div>
      )}

      {/* Add form */}
      <form onSubmit={add} className="mt-4 rounded-2xl bg-[#FF7A00]/8 border border-[#FF7A00]/30 p-3 space-y-2" data-testid="pca-form">
        <div className="flex items-center gap-1.5">
          <Plus className="w-4 h-4 text-[#FF7A00]"/>
          <h3 className="font-bold text-sm">Add Premium Video</h3>
        </div>
        <VideoUploader
          value={video}
          onUploaded={setVideo}
          onCleared={() => setVideo(null)}
          testId="pca-video"
          label="Video file (from gallery)"
        />
        <input data-testid="pca-title" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Video title"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        {msg && <div className={`text-xs ${msg.startsWith("✓") ? "text-emerald-300" : "text-[#FF2A5F]"}`} data-testid="pca-msg">{msg}</div>}
        <button type="submit" disabled={busy || !video} data-testid="pca-add-btn"
          className="w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-1.5">
          <Plus className="w-4 h-4"/>{busy ? "Adding…" : "Publish to this Premium Profile"}
        </button>
      </form>

      {/* List */}
      <div className="mt-4">
        <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">Videos ({content.length})</h3>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]"/></div>
        ) : content.length === 0 ? (
          <div className="text-center py-6 text-white/40 text-xs">No videos yet — upload the first one above.</div>
        ) : (
          <div className="space-y-2" data-testid="pca-content-list">
            {content.map(c => (
              <div key={c.id} data-testid={`pca-row-${c.id}`} className={`flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10 ${c.is_hidden ? "opacity-60" : ""}`}>
                {c.thumbnail_url ? (
                  <img src={c.thumbnail_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0"/>
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-white/10 flex-shrink-0 flex items-center justify-center"><Video className="w-5 h-5 text-white/40"/></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{c.title}</div>
                  <div className="text-[10px] text-white/50 truncate">{c.duration_seconds}s · {c.views || 0} views · 🪙 {c.price_goldly || 0}</div>
                </div>
                <button onClick={() => openEdit(c)} data-testid={`pca-edit-${c.id}`}
                  className="p-2 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] active:scale-90">
                  <Edit3 className="w-4 h-4"/>
                </button>
                <button onClick={() => toggleHide(c)} data-testid={`pca-hide-${c.id}`}
                  className={`p-2 rounded-full border active:scale-90 ${c.is_hidden ? "bg-[#FF7A00]/20 border-[#FF7A00]/40 text-[#FF7A00]" : "bg-white/5 border-white/15 text-white/60"}`}>
                  {c.is_hidden ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
                <button onClick={() => del(c)} data-testid={`pca-del-${c.id}`}
                  className="p-2 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] active:scale-90">
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="pca-edit-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)}/>
          <div className="relative w-full max-w-md rounded-2xl bg-[#0B0F19] border border-white/15 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-outfit font-bold text-lg">Edit Premium Video</h3>
              <button onClick={() => setEditing(null)} data-testid="pca-edit-close" className="p-1.5 rounded-full bg-white/5"><X className="w-4 h-4"/></button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Title</label>
              <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} data-testid="pca-edit-title"
                className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Price (Goldly per view)</label>
                <input type="number" min={0} value={editForm.price_goldly} onChange={e => setEditForm(f => ({ ...f, price_goldly: e.target.value }))} data-testid="pca-edit-price"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Duration (sec)</label>
                <input type="number" min={1} value={editForm.duration_seconds} onChange={e => setEditForm(f => ({ ...f, duration_seconds: e.target.value }))} data-testid="pca-edit-dur"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editForm.is_hidden} onChange={e => setEditForm(f => ({ ...f, is_hidden: e.target.checked }))} data-testid="pca-edit-hidden"/>
              Hide from users
            </label>
            <button onClick={saveEdit} disabled={saving} data-testid="pca-edit-save"
              className="w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
